import { useState, useEffect, useMemo } from 'react';
import intl from 'react-intl-universal';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
// functions
import api from 'utils/api';
import localData from 'utils/localData';
import { getFirstDayPreviousMonth } from 'utils/dateTime';
import logger from 'utils/logger';
// constants
import { KENSHIN_API } from 'shared/constants/apis';
import { USER_DATA } from 'shared/constants/users';

// components and parts
import { PageError } from 'components';
import { useSnackbar } from 'notistack';
import {
  diagnosticResultColumn,
  caseIdColumn,
  patientIdColumn,
  bodyPartColumn,
  modalityColumn,
  studyInstanceUidColumn,
  sourceOrganizationColumn,
  viewerColumn,
} from './assignementColumns';
import KenshinTable from 'components/KenshinTable';
import { TABLE_STATE_KEYS, TABLE_TYPE } from 'shared/constants/tableOptions';

// Create a logger instance for this component
const log = logger.createLogger('KenshinAssignment');

const DashboardReceivedAssignmentKenshin = () => {
  const currentDate = new Date();
  currentDate.setHours(23, 59, 59, 999);
  const firstDayPreviousMonth = getFirstDayPreviousMonth(currentDate);
  const [timeRangeStart, setTimeRangeStart] = useState(() => {
    const saved = sessionStorage.getItem(
      TABLE_STATE_KEYS.KENSHIN_ASSIGNMENT_START_DATE
    );
    return saved ? new Date(saved) : firstDayPreviousMonth;
  });
  const [timeRangeEnd, setTimeRangeEnd] = useState(() => {
    const saved = sessionStorage.getItem(
      TABLE_STATE_KEYS.KENSHIN_ASSIGNMENT_END_DATE
    );
    return saved ? new Date(saved) : currentDate;
  });

  const navigate = useNavigate();
  const location = useLocation();

  const { enqueueSnackbar } = useSnackbar();

  const [error, setError] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [orginalData, setOrginalData] = useState([]);
  const [viewerData, setViewerData] = useState([]);

  const handleOpenViewer = (caseId) => {
    navigate(`${location.pathname}/viewer/${caseId}`);
  };

  // Save time states
  useEffect(() => {
    sessionStorage.setItem(
      TABLE_STATE_KEYS.KENSHIN_ASSIGNMENT_START_DATE,
      timeRangeStart.toISOString()
    );
  }, [timeRangeStart]);

  useEffect(() => {
    sessionStorage.setItem(
      TABLE_STATE_KEYS.KENSHIN_ASSIGNMENT_END_DATE,
      timeRangeEnd.toISOString()
    );
  }, [timeRangeEnd]);

  const columns = useMemo(
    () => [
      sourceOrganizationColumn(),
      caseIdColumn(),
      patientIdColumn(),
      studyInstanceUidColumn(),
      bodyPartColumn(),
      modalityColumn(),
      diagnosticResultColumn(),
      viewerColumn({ handleOpenViewer }),
    ],
    []
  );

  // Kenshin has own pagination in backend
  useEffect(() => {
    const kenshinApiVariables = {
      baseURL: import.meta.env.VITE_KENSHIN_API_URL,
      params: {
        userId: localData.get(USER_DATA.USER_ID),
        organizationId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
        username: localData.get(USER_DATA.USERNAME),
        timeRangeStart: timeRangeStart,
        timeRangeEnd: timeRangeEnd,
      },
    };

    api
      .get(KENSHIN_API.ASSIGNMENT_RECEIVED, kenshinApiVariables)
      .then(async (data) => {
        const { assignments } = data;
        assignments.sort(
          (assignment1, assignment2) => assignment2.id - assignment1.id
        );
        const foundAssignments = assignments.map((assignment, index) => {
          return {
            ...assignment,
            index: index,
          };
        });
        setOrginalData(foundAssignments);
        setViewerData([...foundAssignments]);
        getTableData([...foundAssignments]);
      })
      .catch((_) => {
        setError(true);
      });
  }, [timeRangeStart, timeRangeEnd]);

  if (error) {
    return (
      <PageError
        errorCode={error.response.status}
        customErrorMessage={error.response.data.message}
        customErrorDescription={error.response.data.description}
      />
    );
  }

  const updateLocalAssignments = (caseId, updatedFields) => {
    // The updatedFields reside inside the viewerInfo
    const tempAssignments = [...tableData];
    const index = tempAssignments.findIndex(
      (assignment) => assignment.id === caseId
    );
    tempAssignments[index].viewerInfo = {
      ...tempAssignments[index].viewerInfo,
      ...updatedFields,
    };
    setTableData(tempAssignments);
    handleRefreshClick(false);
  };

  const handleCloseViewer = (directUrl) => {
    navigate(directUrl);
  };

  const handleRefreshClick = async (active = true) => {
    // Updating search times as the table will contain the same issues if time is not updated
    if (timeRangeEnd.toDateString() === new Date().toDateString()) {
      setTimeRangeEnd(new Date());
    }
    if (active) {
      enqueueSnackbar(intl.get('shared_table_data_reload_success'), {
        variant: 'success',
        autoHideDuration: 3000,
      });
    }
  };

  const handleRowModelChange = (rowModel) => {
    const rowData = rowModel.map((row) => row.original);
    const newTableData = rowData.map((row) => {
      const originalRow = orginalData.find((r) => r.id === row.id);
      return {
        ...row,
        ...originalRow,
      };
    });
    setViewerData(newTableData);
  };

  // Will only be called once
  const getTableData = (newAssignments) => {
    const tableAllData = newAssignments.map((assignment) => {
      return {
        caseIdentifierId: assignment.caseIdentifierId,
        srcOrgName: assignment.srcOrgName,
        patientId: assignment.patientId,
        studyInstanceUID: assignment.studyInstanceUID,
        bodypart: assignment.bodypart,
        modality: assignment.modality,
        diagnosis: assignment.diagnosis,
        id: assignment.id,
        viewerInfo: {
          caseId: assignment.id,
        },
      };
    });
    setTableData(tableAllData);
  };

  return (
    <div>
      <KenshinTable
        columns={columns}
        data={tableData}
        tableTitle={intl.get('kenshin_assignment_table_title')}
        timeRangeStart={timeRangeStart}
        timeRangeEnd={timeRangeEnd}
        updateTimeRangeStart={setTimeRangeStart}
        updateTimeRangeEnd={setTimeRangeEnd}
        refreshData={handleRefreshClick}
        handleBatchEditClick={null}
        handleRowModelChange={handleRowModelChange}
        tableType={TABLE_TYPE.KENSHIN_ASSIGNMENT}
      />
      <Outlet
        context={{
          viewerData,
          updateLocalAssignments,
          handleCloseViewer,
        }}
      />
    </div>
  );
};

export default DashboardReceivedAssignmentKenshin;
